import React, { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";
import { ASSETS } from "../i18n/translations";
import { legal } from "../i18n/legal";
import { ArrowLeft } from "lucide-react";

export default function LegalPage({ kind }) {
  const { t, lang, setLang, languages } = useLanguage();
  const navigate = useNavigate();
  const data = legal[lang]?.[kind] || legal.de[kind];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [kind]);

  return (
    <div data-testid={`legal-page-${kind}`} className="min-h-screen bg-[#fafbfd] text-[#0A0A0A]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-xl border-b border-[#1d428a]/15">
        <div className="px-6 md:px-10 lg:px-16">
          <div className="flex items-center justify-between h-20 md:h-24">
            <Link to="/" data-testid="legal-brand-logo" className="flex items-center gap-4 group">
              <img src={ASSETS.logo} alt="Schweizer Planmanufaktur GmbH" className="h-16 md:h-20 w-auto" />
              <div className="hidden md:block leading-tight border-l border-[#1d428a]/30 pl-4">
                <div className="font-display-mid text-xl md:text-2xl text-[#1d428a] tracking-wide">SCHWEIZER</div>
                <div className="font-mono-tech text-[#df4661] mt-1 text-[10px]">PLANMANUFAKTUR GMBH</div>
              </div>
            </Link>
            <div className="flex items-center gap-3">
              <div data-testid="legal-language-switcher" className="hidden sm:flex items-center border border-[#1d428a]">
                {languages.map((l, idx) => (
                  <button
                    key={l.code}
                    onClick={() => setLang(l.code)}
                    data-testid={`legal-lang-btn-${l.code}`}
                    className={`font-mono-tech px-2.5 py-1.5 transition-colors ${
                      lang === l.code ? "bg-[#1d428a] text-white" : "text-[#1d428a] hover:bg-[#1d428a]/10"
                    } ${idx !== languages.length - 1 ? "border-r border-[#1d428a]" : ""}`}
                    type="button"
                  >
                    {l.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => navigate("/")}
                data-testid="legal-back-button"
                className="inline-flex items-center gap-2 bg-[#1d428a] text-white font-mono-tech px-5 py-3 hover:bg-[#df4661] transition-colors"
              >
                <ArrowLeft size={14} strokeWidth={2} />
                <span className="hidden sm:inline">{data.back}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="pt-32 md:pt-36 pb-20">
        <div className="px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-12 gap-y-8 mb-12">
            <div className="col-span-12 md:col-span-4">
              <div className="font-mono-tech text-[#1d428a]">
                <span className="inline-block w-2 h-2 bg-[#df4661] mr-3 align-middle" />
                {kind === "impressum" ? "RECHTLICHE HINWEISE" : "DATENSCHUTZ"}
              </div>
            </div>
            <div className="col-span-12 md:col-span-8">
              <h1
                data-testid={`legal-title-${kind}`}
                className="font-display text-4xl md:text-5xl lg:text-6xl text-[#0A0A0A]"
              >
                {data.title}
              </h1>
              {kind === "datenschutz" && (
                <>
                  <p className="mt-5 text-[#0A0A0A] text-sm md:text-base max-w-3xl leading-relaxed">
                    {data.intro}
                  </p>
                  <div className="font-mono-tech text-[#5A6270] mt-4">{data.last_update}</div>
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-12 gap-10">
            <aside className="col-span-12 md:col-span-4">
              <div className="border border-[#1d428a]/15 bg-white p-5">
                <div className="font-mono-tech text-[#df4661] mb-3">INHALT</div>
                <ul className="space-y-2">
                  {data.sections.map((s, i) => (
                    <li key={i}>
                      <a
                        href={`#sec-${i}`}
                        className="text-sm text-[#0A0A0A] hover:text-[#1d428a] link-underline"
                      >
                        {s.h}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>

            <div className="col-span-12 md:col-span-8 space-y-10">
              {data.sections.map((s, i) => (
                <section key={i} id={`sec-${i}`} className="scroll-mt-32">
                  <div className="flex items-baseline gap-3 mb-3">
                    <span className="font-mono-tech text-[#df4661]">{String(i + 1).padStart(2, "0")}</span>
                    <h2 className="font-display-mid text-xl md:text-2xl text-[#1d428a]">{s.h}</h2>
                  </div>
                  <div className="space-y-2.5">
                    {s.body.map((p, j) => (
                      <p key={j} className="text-sm md:text-[15px] leading-relaxed text-[#0A0A0A]">
                        {p}
                      </p>
                    ))}
                  </div>
                  <div className="mt-6 h-px bg-[#1d428a]/15" />
                </section>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* Compact footer */}
      <footer className="border-t border-[#1d428a]/20 bg-white">
        <div className="px-6 md:px-10 lg:px-16 py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 font-mono-tech text-[#5A6270]">
          <div>© {new Date().getFullYear()} SCHWEIZER PLANMANUFAKTUR GMBH</div>
          <div className="flex gap-6">
            <Link to="/impressum" className="hover:text-[#1d428a]">{t.footer.imprint}</Link>
            <Link to="/datenschutz" className="hover:text-[#1d428a]">{t.footer.privacy}</Link>
            <Link to="/" className="hover:text-[#1d428a]">{data.back}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
